import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Landmark, ShieldCheck, Copy, Check, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/finance`;

const CopyChip = ({ label, value }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const onCopy = async () => {
    try { await navigator.clipboard.writeText(String(value)); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (_) {}
  };
  return (
    <div className="flex items-center justify-between border rounded-lg px-3 py-2 bg-white gap-3">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
        <div className="font-mono text-slate-800 truncate">{value}</div>
      </div>
      <button type="button" onClick={onCopy} className="text-slate-500 hover:text-slate-800 shrink-0" aria-label={`Copy ${label}`}>
        {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
};

const PublicPay = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/public/pay/${encodeURIComponent(token)}`);
        setData(r.data);
      } catch (e) {
        setErr(e?.response?.data?.detail || 'This link is invalid or has expired.');
      } finally { setLoading(false); }
    })();
  }, [token]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }

  if (err || !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="public-pay-error">
        <Card className="max-w-md border-red-200 bg-red-50">
          <CardContent className="p-6 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
            <div>
              <p className="font-medium text-red-800">Link unavailable</p>
              <p className="text-sm text-red-700 mt-1">{err || 'Please request a new link from the invoicing team.'}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const b = data.banking || {};

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="public-pay-page">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-start gap-3">
          <Landmark className="h-7 w-7 text-blue-700 mt-1" />
          <div>
            <h1 className="text-2xl font-semibold text-slate-800">Payment Instructions</h1>
            <p className="text-sm text-slate-500 max-w-xl mt-1">
              {data.vendor_name || 'USKE Supply LLC'} · Secure invoice-specific banking details.
              This link is time-limited — do not share beyond your treasury team.
            </p>
          </div>
        </div>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Invoice</CardTitle></CardHeader>
          <CardContent className="grid sm:grid-cols-3 gap-3">
            <CopyChip label="Invoice Number" value={data.invoice_no} />
            <CopyChip label="Total" value={`$${Number(data.total || 0).toFixed(2)} ${data.currency || 'USD'}`} />
            <CopyChip label="Balance Due" value={`$${Number(data.balance_due || 0).toFixed(2)}`} />
            <CopyChip label="Issue Date" value={data.issue_date} />
            <CopyChip label="Due Date" value={data.due_date} />
            <CopyChip label="Terms" value={data.payment_terms} />
          </CardContent>
        </Card>

        <div className="flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-md p-3">
          <ShieldCheck className="h-4 w-4 shrink-0" />
          {b.note}
        </div>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">ACH (Preferred · No fees)</CardTitle></CardHeader>
          <CardContent className="grid sm:grid-cols-2 gap-3">
            <CopyChip label="Beneficiary" value={b.beneficiary} />
            <CopyChip label="Account Type" value={b.ach?.type} />
            <CopyChip label="Routing Number" value={b.ach?.routing} />
            <CopyChip label="Account Number" value={b.ach?.account} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Wire Transfer</CardTitle></CardHeader>
          <CardContent className="grid sm:grid-cols-2 gap-3">
            <CopyChip label="Beneficiary" value={b.beneficiary} />
            <CopyChip label="Beneficiary Address" value={b.beneficiary_address} />
            <CopyChip label="Bank Name" value={b.wire?.bank_name} />
            <CopyChip label="Bank Address" value={b.wire?.bank_address} />
            <CopyChip label="Routing / ABA" value={b.wire?.routing} />
            <CopyChip label="Account Number" value={b.wire?.account} />
            <CopyChip label="SWIFT / BIC" value={b.wire?.swift} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Cheque</CardTitle></CardHeader>
          <CardContent className="grid sm:grid-cols-2 gap-3">
            <CopyChip label="Payable To" value={b.cheque?.payable_to} />
            <CopyChip label="Mail To" value={b.cheque?.mail_to} />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 text-sm text-slate-600 space-y-1">
            <p><b>Reference:</b> {data.memo}</p>
            <p>
              Support: <a className="text-blue-700 underline" href={`mailto:${b.support_email || 'info@uskesupplyllc.com'}`}>{b.support_email || 'info@uskesupplyllc.com'}</a>
              {b.support_phone && <> · {b.support_phone}</>}
            </p>
          </CardContent>
        </Card>

        <p className="text-[11px] text-slate-400 text-center pt-4">
          This page is served to holders of a signed link. It does not require login and displays no other customer data.
        </p>
      </div>
    </div>
  );
};

export default PublicPay;
